import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
} from "@mui/material";
import {
  ConfirmDialogHost,
  openConfirmDialog,
} from "@/components/ConfirmDialog";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import {
  Avatar,
  Checkbox,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  Modal,
  Stack,
  useTheme,
  Button,
} from "@ui";
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import type { Company, Document } from "@/pages/private/dashboard/SharePage";
import { declarationEndpoints } from "@/shared/api/endpoints";
import { httpGet, httpPost, httpPut } from "@/shared/api/http";
import {
  ApiResponse,
  CustomerDeclarationEntry,
  DeclarationAllEntry,
  SharedDocResponse,
  UpdateDocumentRequest,
} from "@/shared/api/types";
import femaCapture from "@/shared/fema/capture";
import { formatDate } from "@/shared/lib/utils/formatDate";
import { DocumentsPageService } from "@/shared/api/services/documentsPageService";
import { ThemeColours } from "@/clients/acme/config/colours";
import { useAlert } from "@/app/providers/AlertProvider";
import { inferMimeType } from "@/shared/lib/utils/documents";
import type { UploadedDocument } from "@/pages/private/dashboard/DocumentPage";
import DocumentsList from "./DocumentsList";
import { SelectableCard } from "./UUDeclarationDialog";
import { getCompanyDocs } from "@/shared/share/sharePageUtils";
import { isAllowedEntrySource } from "@/constants/documents";

interface YWCTDeclarationDialogProps {
  onClose: () => void;
  company: Company;
  setCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
  toggleCompanyShare: (company: Company) => Promise<void>;
}

const YWCTDeclarationDialog: React.FC<YWCTDeclarationDialogProps> = ({
  onClose,
  company,
  setCompanies,
  toggleCompanyShare,
}) => {
  const theme = useTheme();
  const { showAlert } = useAlert();

  const [isUploading, setIsUploading] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState(false);
  const [processStoreDetails, setProcessStoreDetails] = useState(false);
  const [neededPersonalDetails, setNeededPersonalDetails] = useState(false);
  const [documentConfirmatinYwct, setDocumentConfirmatinYwct] = useState(false);
  const [awardNotRcvdConfirmationYwct, setAwardNotRcvdConfirmationYwct] =
    useState(false);

  const [status, setStatus] = useState("");
  const [isNewData, setIsNewData] = useState(true);
  const [raw, setRaw] = useState<CustomerDeclarationEntry>();

  const [documents, setDocuments] = useState<Document[]>([]);
  const [docsChange, setDocsChange] = useState<boolean>(false);

  const [openPreview, setOpenPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewFileType, setPreviewFileType] = useState<string | null>(null);
  const [previewFileName, setPreviewFileName] = useState<string | null>(null);

  const handleOpenPreview = async (doc: UploadedDocument) => {
    try {
      setLoadingPreview(true);
      setOpenPreview(true);
      setPreviewFileName(doc.name);

      console.log("blobdat", doc);

      const data = await DocumentsPageService.downloadCustomerDocument(
        doc.apiObject?.documentId
      );

      const blob =
        data instanceof Blob
          ? new Blob([data], { type: data.type || inferMimeType(doc.name) })
          : new Blob([data as unknown as BlobPart], {
              type: inferMimeType(doc.name),
            });

      const blobUrl = URL.createObjectURL(blob);

      setPreviewBlobUrl(blobUrl);
      setPreviewFileType(blob.type || inferMimeType(doc.name));
    } catch (err) {
      console.error("Preview failed:", err);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setLoadingPreview(false);
    setOpenPreview(false);
  };

  const radioCards = [
    {
      title: "Yes, I accept",
      description:
        "I give explicit consent for Yorkshire Water Community Trust.",
    },
    {
      title: "Reject",
      description:
        "I do not give consent for IE Hub to add or share my response.",
    },
  ];

  const handleDeleteDocument = async (id: string) => {
    try {
      const yes = await openConfirmDialog({
        title: "Delete Document",
        message: "Are you sure you want to delete this?",
        confirmText: "Delete",
        cancelText: "Cancel",
      });

      if (!yes) {
        return;
      }

      const customerData = JSON.parse(
        localStorage.getItem("customerData") as string
      );
      await DocumentsPageService.deleteCustomerDocument(
        customerData.customerId,
        id
      );
      setDocuments((prev) => prev.filter((doc) => doc.id !== id));
    } catch (e) {
      console.log("An error occurred when deleting document with id:", id);
    }
  };

  const handleDocumentInput = async (id: string, value: string) => {
    try {
      setDocuments((prev) =>
        prev.map((doc) => {
          if (doc.id == id) {
            doc.description = value;
          }

          return doc;
        })
      );

      setDocsChange(true);
    } catch (e) {
      console.log("An error occurred when editing document with id:", id);
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(async () => {
    if (isUploading) {
      return;
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    // Trigger file input click
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.onchange = async (e: Event) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (documents.length + files.length > 10) {
        showAlert({
          severity: "warning",
          message:
            "Looks like you’ve hit the max! You can upload up to 10 documents.",
          autoHide: true,
          duration: 5000,
        });
        return;
      }

      const allowedExtensions = ["pdf", "png", "jpg", "jpeg"];

      for (const file of files) {
        const ext = file.name.split(".").pop()?.toLowerCase();

        if (!ext || !allowedExtensions.includes(ext)) {
          showAlert({
            severity: "error",
            message:
              "Invalid file format. You can only upload PDF, PNG, JPG, or JPEG files.",
            duration: 3000,
          });
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          showAlert({
            severity: "error",
            message:
              "The file is too large. Please select a file smaller than 5MB.",
            duration: 3000,
          });
          return;
        }
      }

      setIsUploading(true);
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);

        const response = await DocumentsPageService.uploadCustomerDocument(
          customerData.customerId,
          formData,
          "Yorkshire Water Community Trust",
          ""
        );

        const {
          id,
          documentSize,
          fileName,
          creationDate,
          documentName,
          documentId,
        } = response;

        console.log("blobdat", response)

        setDocuments((prev) => [
          ...prev,
          {
            id: String(id),
            name: fileName,
            documentId,
            size: documentSize,
            description: documentName,
            uploadedDate: formatDate(creationDate),
            type: "wage-slip",
            isNewlyUploaded: true,
            apiObject: response,
          },
        ]);
      }

      setDocsChange(true);

      showAlert({
        severity: "success",
        message: `Successfully uploaded ${files.length} document${files.length > 1 ? "s" : ""}`,
        autoHide: true,
        duration: 5000,
      });

      setIsUploading(false);
    };

    input.click();
  }, [documents, isUploading]);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (isUploading) {
        return;
      }

      if (documents.length >= 10) {
        showAlert({
          severity: "warning",
          message:
            "Looks like you’ve hit the max! You can upload up to 10 documents.",
          autoHide: true,
          duration: 5000,
        });
        return;
      }

      const customerData = JSON.parse(
        localStorage.getItem("customerData") as string
      );

      const files = Array.from(e.dataTransfer.files);

      if (documents.length + files.length > 10) {
        showAlert({
          severity: "warning",
          message:
            "Looks like you’ve hit the max! You can upload up to 10 documents.",
          autoHide: true,
          duration: 5000,
        });
        return;
      }

      const allowedExtensions = ["pdf", "png", "jpg", "jpeg"];

      for (const file of files) {
        const ext = file.name.split(".").pop()?.toLowerCase();

        if (!ext || !allowedExtensions.includes(ext)) {
          showAlert({
            severity: "error",
            message:
              "Invalid file format. You can only upload PDF, PNG, JPG, or JPEG files.",
            duration: 3000,
          });
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          showAlert({
            severity: "error",
            message:
              "The file is too large. Please select a file smaller than 5MB.",
            duration: 3000,
          });
          return;
        }
      }

      setIsUploading(true);
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);

        const response = await DocumentsPageService.uploadCustomerDocument(
          customerData.customerId,
          formData,
          "United Utilities",
          ""
        );

        const {
          id,
          documentSize,
          fileName,
          creationDate,
          documentName,
          documentId,
        } = response;

        setDocuments((prev) => [
          ...prev,
          {
            id: String(id),
            name: fileName,
            documentId,
            size: documentSize,
            description: documentName,
            uploadedDate: formatDate(creationDate),
            type: "wage-slip",
            isNewlyUploaded: true,
            apiObject: response,
          },
        ]);
      }

      showAlert({
        severity: "success",
        message: `Successfully uploaded ${files.length} document${files.length > 1 ? "s" : ""}`,
        autoHide: true,
        duration: 5000,
      });

      setIsUploading(false);
    },
    [documents, isUploading]
  );

  const fetchDocuments = async (): Promise<UploadedDocument[]> => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    try {
      const data = await DocumentsPageService.getCustomerDocuments(
        customerData.customerId
      );

      const documentsArray = Array.isArray(data)
        ? data
        : (data as ApiResponse<any[]>).data || [];

      return documentsArray.map((doc: any) => ({
        id: String(doc.id),
        name: doc.fileName,
        documentName: doc.documentName,
        documentId: doc.documentId,
        size: String(doc.documentSize || ""),
        description: doc.documentName,
        uploadDate: formatDate(doc.creationDate),
        progress: 100,
        status: "completed",
        type: "wage-slip" as const,
        isNewlyUploaded: true,
        apiObject: doc as UpdateDocumentRequest,
      }));
    } catch (error) {
      console.error("Failed to fetch companies", error);
      return [];
    }
  };

  // Fetch documents
  useEffect(() => {
    fetchDocuments().then((documentsArray) => {
      const filteredDocs = documentsArray.filter((c) => {
        const apiObj = c.apiObject as UpdateDocumentRequest & {
          entrySource?: string;
        };
        return isAllowedEntrySource(apiObj.entrySource);
      });
      setDocuments(
        filteredDocs.map((doc) => {
          const apiObj = doc.apiObject as UpdateDocumentRequest;
          return {
            id: doc.id,
            name: doc.name,
            documentId: apiObj.documentId,
            size: doc.size,
            description: doc.documentName || "",
            uploadedDate: doc.uploadDate,
            type: "wage-slip" as const,
            isNewlyUploaded: true,
            raw: apiObj as SharedDocResponse,
          };
        })
      );
    });
  }, []);

  // GET declaration data all
  useEffect(() => {
    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    async function getDeclarationDataAll() {
      try {
        const response = await httpGet<ApiResponse>(
          declarationEndpoints.getDeclarationAll(customerData.customerId)
        );

        const data: any = response?.data;

        const res = data.find((entry: CustomerDeclarationEntry) => {
          return String(company.id) === String(entry.sharingId);
        });

        const {
          confirmDetails,
          processStoreDetails,
          neededPersonalDetails,
          documentConfirmatinYwct,
          awardNotRcvdConfirmationYwct,
          declarationStatus,
        } = res;

        setConfirmDetails(confirmDetails == "Y");
        setProcessStoreDetails(processStoreDetails == "Y");
        setNeededPersonalDetails(neededPersonalDetails == "Y");
        setDocumentConfirmatinYwct(documentConfirmatinYwct == "Y");
        setAwardNotRcvdConfirmationYwct(awardNotRcvdConfirmationYwct == "Y");

        if (company.declarationStatus == 'not-completed') {
          setStatus("");
        } else {
          setStatus(declarationStatus == "Accept" ? "Yes, I accept" : "");
        }

        setRaw(res);

        setIsNewData(false);
      } catch (error) {
        femaCapture("fetching_declaration_all_data_error", error, {
          category: "api",
          severity: "error",
          skipSanitization: false,
          user: {},
        });
        throw error;
      }
    }

    getDeclarationDataAll();
  }, []);

  async function fetchCompanyDocuments(company: Company): Promise<{
    newDocs: Document[];
    selectedDocuments: string[];
  }> {
    const lenderName = company.raw?.lenderName || "";
    const accountNumber = company.raw?.accountNumber || "";

    const companyDocsResponse = await getCompanyDocs(
      company.id,
      lenderName,
      accountNumber,
    );

    const companyDocsArray: SharedDocResponse[] = Array.isArray(companyDocsResponse)
      ? companyDocsResponse
      : (companyDocsResponse as ApiResponse<any[]>)?.data || [];

    const newDocs: Document[] = companyDocsArray.map((doc) => ({
      id: String(doc.id),
      name: doc.documentName || doc.fileName,
      size: doc.documentSize,
      type: "wage-slip",
      uploadedDate: formatDate(doc.lastUpdateDate),
      raw: doc,
    }));

    const selectedDocuments = newDocs
      .filter((doc) => doc?.raw?.isShared === "Y")
      .map((doc) => doc.id);

    return { newDocs, selectedDocuments };
  }

  async function handleClose() {
    const { newDocs, selectedDocuments } = await fetchCompanyDocuments(company);

    setCompanies((prev) => {
      prev.map(async (c) => {
        if (c.id === company.id) {
          c.documents = newDocs;
          c.selectedDocuments = selectedDocuments;
        }

        return c;
      });

      return prev;
    });

    onClose();
  }

  async function handleSave() {
    if (
      status != "Reject" &&
      !(
        status &&
        confirmDetails &&
        processStoreDetails &&
        neededPersonalDetails &&
        documentConfirmatinYwct &&
        awardNotRcvdConfirmationYwct
      )
    ) {
      showAlert({
        severity: "warning",
        message: "Please fill out all required fields.",
        autoHide: true,
        duration: 2500,
      });
      return;
    }

    const customerData = JSON.parse(
      localStorage.getItem("customerData") as string
    );

    let newCompanyDocs: Document[] = [];

    if (docsChange) {
      for (const doc of documents) {
        const payload = {
          id: doc.id,
          customerId: customerData.customerId,
          documentId: Number(doc.documentId),
          documentName: doc.description as string,
          documentSize: doc.size,
          fileName: doc.name,
        };

        const docResp: SharedDocResponse = await DocumentsPageService.updateCustomerDocument(payload);

        newCompanyDocs.push({
          id: doc.id,
          name: docResp.documentName || docResp.fileName,
          size: doc.size,
          type: "wage-slip",
          uploadedDate: formatDate(docResp.lastUpdateDate),
          raw: docResp,
        });
      }
    }

     const { newDocs, selectedDocuments } = await fetchCompanyDocuments(company);
    

    if (isNewData) {
      const payload = {
        customerId: customerData.customerId,
        sharingId: company.id,
        declarationStatus: status == "Yes, I accept" ? "Accept" : "Reject",
        confirmDetails: confirmDetails ? "Y" : "N",
        processStoreDetails: processStoreDetails ? "Y" : "N",
        neededPersonalDetails: neededPersonalDetails ? "Y" : "N",
        documentConfirmatinYwct: documentConfirmatinYwct ? "Y" : "N",
        awardNotRcvdConfirmationYwct: awardNotRcvdConfirmationYwct ? "Y" : "N",
        companyName: "Yorkshire Water Community Trust",
        accountNumber: company.reference,
      };

      await httpPost<DeclarationAllEntry>(
        declarationEndpoints.createDeclarationAll(),
        payload
      );
    } else {
      const payload = {
        ...raw,
        declarationStatus: status == "Yes, I accept" ? "Accept" : "Reject",
        confirmDetails: confirmDetails ? "Y" : "N",
        processStoreDetails: processStoreDetails ? "Y" : "N",
        neededPersonalDetails: neededPersonalDetails ? "Y" : "N",
        documentConfirmatinYwct: documentConfirmatinYwct ? "Y" : "N",
        awardNotRcvdConfirmationYwct: awardNotRcvdConfirmationYwct ? "Y" : "N",
      };

      await httpPut<CustomerDeclarationEntry>(
        declarationEndpoints.createDeclarationAll(),
        payload
      );
    }

    // set shareNow to N if rejected is selected (For UU, this is handled by the backend)
    if (status == 'Reject') {
      toggleCompanyShare(company);
    }

    setCompanies((prev) => {
      prev.map((c) => {
        if (c.id === company.id) {
          c.declarationStatus =
            status == "Yes, I accept" ? "accepted" : "rejected";

          if (c.declarationStatus == "rejected" && c.raw) {
            c.raw.shareNow = "N";
          } else if (c.declarationStatus == "accepted" && c.raw) {
            c.raw.shareNow = "Y";
          }

          c.documents = newDocs;
          c.selectedDocuments = selectedDocuments;
        }

        return c;
      });

      return prev;
    });

    onClose();
  }

  return (
    <>
      <ConfirmDialogHost />
      <Box data-testid="components-ywctdeclaration-dialog-container-action-1" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Dialog Title */}
        <DialogTitle data-testid="components-ywctdeclaration-dialog-header-action"
          sx={{
            borderBottom: "1px solid #e0e0e0",
            px: 4,
            justifyContent: "space-between",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography data-testid="components-ywctdeclaration-dialog-text-yorkshire-water-community-trust-declaration" variant="h5" sx={{ fontWeight: 600 }}>
            Yorkshire Water Community Trust Declaration
          </Typography>

          <IconButton data-testid="components-ywctdeclaration-dialog-btn-handle-close"
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme: ReturnType<typeof useTheme>) =>
                theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent sx={{ pt: 3, px: 4, flexGrow: 1, overflowY: "auto" }}>
          <Box data-testid="components-ywctdeclaration-dialog-container-action-2"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#E8EFEE",
              border: "1px solid #165F54",
              borderRadius: 1,
              mb: 2,
              mt: 2.5,
            }}
          >
            <Checkbox
              size="medium"
              sx={{ mr: 0.5 }}
              checked={confirmDetails}
              onChange={() => setConfirmDetails(!confirmDetails)}
            />
            <Typography data-testid="components-ywctdeclaration-dialog-text-action-1" variant="h6" sx={{ fontWeight: 500, mt: 0.4 }}>
              I confirm the details provided are true to the best of my
              knowledge
              <Box data-testid="components-ywctdeclaration-dialog-container-*-1" component="span" sx={{ color: "red", ml: 0.5 }}>
                *
              </Box>
            </Typography>
          </Box>

          <Box data-testid="components-ywctdeclaration-dialog-container-action-3"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#E8EFEE",
              border: "1px solid #165F54",
              borderRadius: 1,
              mb: 2,
              mt: 2.5,
            }}
          >
            <Checkbox
              size="medium"
              sx={{ mr: 0.5 }}
              checked={processStoreDetails}
              onChange={() => setProcessStoreDetails(!processStoreDetails)}
            />
            <Typography data-testid="components-ywctdeclaration-dialog-text-action-2" variant="h6" sx={{ fontWeight: 500, mt: 0.4 }}>
              I understand that the Trust will process and store my personal
              and, sometimes, sensitive information in accordance with GDPR and
              Trust criteria
              <Box data-testid="components-ywctdeclaration-dialog-container-*-2" component="span" sx={{ color: "red", ml: 0.5 }}>
                *
              </Box>
            </Typography>
          </Box>

          <Box data-testid="components-ywctdeclaration-dialog-container-action-4"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#E8EFEE",
              border: "1px solid #165F54",
              borderRadius: 1,
              mb: 2,
              mt: 2.5,
            }}
          >
            <Checkbox
              size="medium"
              sx={{ mr: 0.5 }}
              checked={neededPersonalDetails}
              onChange={() => setNeededPersonalDetails(!neededPersonalDetails)}
            />
            <Typography data-testid="components-ywctdeclaration-dialog-text-action-3" variant="h6" sx={{ fontWeight: 500, mt: 0.4 }}>
              I agree to the following personal details being shared with
              Yorkshire Water
              <Box data-testid="components-ywctdeclaration-dialog-container-*-3" component="span" sx={{ color: "red", ml: 0.5 }}>
                *
              </Box>{" "}
              :
              <List
                component="ul"
                sx={{
                  listStyleType: "disc",
                  pl: 2.2,
                }}
              >
                <ListItem data-testid="components-ywctdeclaration-dialog-list-item-name" component="li" sx={{ display: "list-item", p: 0 }}>
                  Name
                </ListItem>

                <ListItem data-testid="components-ywctdeclaration-dialog-list-item-address" component="li" sx={{ display: "list-item", p: 0 }}>
                  Address
                </ListItem>

                <ListItem data-testid="components-ywctdeclaration-dialog-list-item-yorkshire-water-reference-number" component="li" sx={{ display: "list-item", p: 0 }}>
                  Yorkshire Water Reference Number
                </ListItem>

                <ListItem data-testid="components-ywctdeclaration-dialog-list-item-action-1" component="li" sx={{ display: "list-item", p: 0 }}>
                  Trust application outcome status (pending/awarded/rejected)
                </ListItem>

                <ListItem data-testid="components-ywctdeclaration-dialog-list-item-award-amount-(if-applicable)" component="li" sx={{ display: "list-item", p: 0 }}>
                  Award amount (if applicable)
                </ListItem>
              </List>
            </Typography>
          </Box>

          <Box data-testid="components-ywctdeclaration-dialog-container-action-5"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#fff",
              border: "1px solid #ddd",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Stack data-testid="components-ywctdeclaration-dialog-container-action-6" sx={{ width: "100%" }}>
              <Typography data-testid="components-ywctdeclaration-dialog-text-upload-documents-1" sx={{ fontWeight: 400, mb: 0, fontSize: "18px" }}>
                Upload documents
              </Typography>
              <Typography data-testid="components-ywctdeclaration-dialog-text-action-4"
                sx={{
                  fontWeight: 400,
                  mb: 0,
                  fontSize: 14,
                  color: ThemeColours.subtitleVar1,
                }}
              >
                Please upload your supporting documents (you will need this for
                a successful claim)
              </Typography>
            </Stack>
          </Box>

          {/* File Upload Section */}
          <Box data-testid="components-ywctdeclaration-dialog-container-action-7" sx={{ mb: 2 }}>
            <Box data-testid="components-ywctdeclaration-dialog-container-handle-file-select"
              onClick={handleFileSelect}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              sx={{
                border: isDragging ? "2px solid #0d9488" : "2px dashed #000",
                borderRadius: 1,
                p: 2,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <Avatar
                sx={{
                  bgcolor: "#DCFCE7",
                  width: 64,
                  height: 64,
                  mb: 1,
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 30, color: "#165F54" }} />
              </Avatar>

              <Typography data-testid="components-ywctdeclaration-dialog-text-drag-&-drop-files-here" sx={{ fontWeight: 500, fontSize: 18, mb: 0 }}>
                Drag & drop files here
              </Typography>
              <Typography data-testid="components-ywctdeclaration-dialog-text-or-tap-to-browse-your-device"
                sx={{ fontWeight: 400, fontSize: 13, color: "#4B5563", mb: 1 }}
              >
                or tap to browse your device
              </Typography>
              <Button data-testid="components-ywctdeclaration-dialog-btn-share-securely"
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontWeight: 400,
                  mb: 1,
                  py: 1.3,
                  px: 3.5,
                  boxShadow: "none",
                  backgroundColor: "#E8EFEE",
                  color: "#6C7A7B",
                  "&:hover": {
                    backgroundColor: "#ccd4d3ff",
                    boxShadow: "none",
                  },
                }}
                endIcon={
                  isUploading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : undefined
                }
              >
                Share Securely
              </Button>
              <Typography data-testid="components-ywctdeclaration-dialog-text-pdf,-jpg,-png-up-to-5-mb" variant="caption" sx={{ color: "#9CA3AE" }}>
                PDF, JPG, PNG up to 5 MB
              </Typography>
            </Box>

            <Modal data-testid="components-ywctdeclaration-dialog-modal-action" open={openPreview} onClose={handleClosePreview}>
              <Box data-testid="components-ywctdeclaration-dialog-container-action-8"
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  bgcolor: "background.paper",
                  boxShadow: 24,
                  borderRadius: 2,
                  p: 2,
                  maxWidth: "90%",
                  maxHeight: "90%",
                  overflow: "auto",
                  textAlign: "center",
                }}
              >
                {loadingPreview ? (
                  <Typography data-testid="components-ywctdeclaration-dialog-text-loading-preview...">Loading preview...</Typography>
                ) : previewBlobUrl ? (
                  (() => {
                    const fileType =
                      previewFileType ||
                      inferMimeType(previewFileName || undefined);

                    const isImage =
                      fileType.startsWith("image/") ||
                      previewFileName?.match(/\.(jpeg|jpg|png|gif|webp)$/i);

                    const isPDF =
                      fileType === "application/pdf" ||
                      previewFileName?.toLowerCase().endsWith(".pdf");

                    if (isImage) {
                      return (
                        <img
                          src={previewBlobUrl}
                          alt="preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "80vh",
                            borderRadius: 8,
                            display: "block",
                            margin: "0 auto",
                          }}
                        />
                      );
                    } else if (isPDF) {
                      return (
                        <iframe
                          src={previewBlobUrl}
                          title="PDF Preview"
                          style={{
                            width: "80vw",
                            height: "80vh",
                            border: "none",
                            borderRadius: 8,
                          }}
                        />
                      );
                    } else {
                      return (
                        <Box data-testid="components-ywctdeclaration-dialog-container-action-9">
                          <Typography data-testid="components-ywctdeclaration-dialog-text-file-type-not-supported-for-inline-preview." mb={2}>
                            File type not supported for inline preview.
                          </Typography>
                          <a data-testid="components-ywctdeclaration-dialog-link-download-file"
                            href={previewBlobUrl}
                            download={previewFileName || "download"}
                            style={{
                              display: "inline-block",
                              padding: "8px 16px",
                              background: "#0d9488",
                              color: "#fff",
                              borderRadius: 6,
                              textDecoration: "none",
                            }}
                          >
                            Download File
                          </a>
                        </Box>
                      );
                    }
                  })()
                ) : (
                  <Typography data-testid="components-ywctdeclaration-dialog-text-failed-to-load-preview." color="error">Failed to load preview.</Typography>
                )}
              </Box>
            </Modal>

            <input data-testid="components-ywctdeclaration-dialog-input-file-upload-input"
              id="file-upload-input"
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
            />
          </Box>

          <Stack data-testid="components-ywctdeclaration-dialog-container-action-10" spacing={2} sx={{ mb: 2 }}>
            <DocumentsList
              notesField="description"
              documents={documents.map((doc) => {
                const docWithNotes = doc as Document & { showNotes?: boolean };
                const showNotesValue = docWithNotes.showNotes;
                return {
                  id: doc.id,
                  name: doc.name,
                  size: doc.size,
                  uploadedDate: doc.uploadedDate,
                  description: doc.description ?? "",
                  toggleNotes: (id: string | number, value: boolean) => {
                    setDocuments((prev) =>
                      prev.map((d) =>
                        String(d.id) === String(id)
                          ? ({ ...d, showNotes: value } as Document & {
                              showNotes?: boolean;
                            })
                          : d
                      )
                    );
                  },
                  ...(showNotesValue !== undefined && {
                    showNotes: showNotesValue,
                  }),
                };
              })}
              onOpen={(doc) => {
                const foundDoc = documents.find(
                  (d) => String(d.id) === String(doc.id)
                );
                console.log("foundDoc", foundDoc);
                const uploadedDoc: UploadedDocument = {
                  id: String(doc.id),
                  name: doc.name,
                  documentName: doc.description || doc.name,
                  size: String(doc.size || ""),
                  uploadDate: doc.uploadedDate || "",
                  progress: 100,
                  status: "completed",
                  apiObject: foundDoc.apiObject as unknown || (foundDoc?.raw as UpdateDocumentRequest) ||
                    ({} as UpdateDocumentRequest),
                };
                return handleOpenPreview(uploadedDoc);
              }}
              onDelete={(id: string | number) =>
                handleDeleteDocument(String(id))
              }
              onNameChange={(doc, newDescription) =>
                handleDocumentInput(String(doc.id), newDescription)
              }
            />
          </Stack>

          {documents.length >= 10 && (
            <Box data-testid="components-ywctdeclaration-dialog-container-action-11"
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                pt: 1.5,
                pb: 2,
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: 1,
                mb: 3,
              }}
            >
              <Stack data-testid="components-ywctdeclaration-dialog-container-action-12" sx={{ width: "100%" }}>
                <Typography data-testid="components-ywctdeclaration-dialog-text-upload-documents-2" sx={{ fontWeight: 400, mb: 0, fontSize: "18px" }}>
                  Upload documents
                </Typography>
                <List
                  component="ul"
                  sx={{
                    listStyleType: "disc",
                    pl: 3,
                    color: "#4B5563",
                  }}
                >
                  <ListItem data-testid="components-ywctdeclaration-dialog-list-item-action-2" component="li" sx={{ display: "list-item", p: 0 }}>
                    You have reached the limit of 10 documents. To continue,
                    please remove some of your existing documents.
                  </ListItem>
                </List>
              </Stack>
            </Box>
          )}

          <Box data-testid="components-ywctdeclaration-dialog-container-action-13"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#E8EFEE",
              border: "1px solid #165F54",
              borderRadius: 1,
              mb: 2,
              mt: 2.5,
            }}
          >
            <Checkbox
              size="medium"
              sx={{ mr: 0.5 }}
              checked={documentConfirmatinYwct}
              onChange={() =>
                setDocumentConfirmatinYwct(!documentConfirmatinYwct)
              }
            />
            <Typography data-testid="components-ywctdeclaration-dialog-text-action-5" variant="h6" sx={{ fontWeight: 500, mt: 0.4 }}>
              I understand Yorkshire Water Community Trust need proof of my
              income to be assessed for their support. If I haven’t uploaded
              this in IE Hub, I can provide it to The Trust separately 
              <Box data-testid="components-ywctdeclaration-dialog-container-*-4" component="span" sx={{ color: "red", ml: 0.5 }}>
                *
              </Box>
            </Typography>
          </Box>

          <Box data-testid="components-ywctdeclaration-dialog-container-action-14"
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              px: 2,
              pt: 1.5,
              pb: 2,
              backgroundColor: "#E8EFEE",
              border: "1px solid #165F54",
              borderRadius: 1,
              mb: 2,
              mt: 2.5,
            }}
          >
            <Checkbox
              size="medium"
              sx={{ mr: 0.5 }}
              checked={awardNotRcvdConfirmationYwct}
              onChange={() =>
                setAwardNotRcvdConfirmationYwct(!awardNotRcvdConfirmationYwct)
              }
            />
            <Typography data-testid="components-ywctdeclaration-dialog-text-action-6" variant="h6" sx={{ fontWeight: 500, mt: 0.4 }}>
              I confirm I haven’t received an award from Yorkshire Water
              Community Trust in the last 5 years
              <Box data-testid="components-ywctdeclaration-dialog-container-*-5" component="span" sx={{ color: "red", ml: 0.5 }}>
                *
              </Box>
            </Typography>
          </Box>

          <Box data-testid="components-ywctdeclaration-dialog-container-action-15"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              my: 3,
            }}
          >
            <Link data-testid="components-ywctdeclaration-dialog-link-yorkshire-water-community-trust-privacy-statement"
              href="/assets/yorkshire-water-community-trust-privacy.pdf"
              sx={{
                color: "#1E40AF",
                fontWeight: 500,
                fontSize: "1rem",
                textDecorationColor: "#1E40AF",
              }}
              target="_blank"
            >
              Yorkshire Water Community Trust Privacy Statement
            </Link>
            <ArrowForwardIcon sx={{ color: "#1E665B", fontSize: 20 }} />{" "}
          </Box>

          <Box data-testid="components-ywctdeclaration-dialog-container-action-16">
            {radioCards.map((card) => (
              <SelectableCard
                key={card.title}
                title={card.title}
                description={card.description}
                selected={status}
                onSelect={setStatus}
              />
            ))}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 4,
            py: 2,
            borderTop: "1px solid #e0e0e0",
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            zIndex: 1,
            boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Button data-testid="components-ywctdeclaration-dialog-btn-save"
            variant="contained"
            color="primary"
            sx={{ textTransform: "none" }}
            size="large"
            onClick={() => {
              handleSave();
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Box>
    </>
  );
};

export default YWCTDeclarationDialog;
